/**
 * usePlan composable - reactive plan status helpers for Vue components
 *
 * Works with pre-computed planStatus/usageStatus from API responses.
 * For computing these values client-side, use getPlanStatus/getUsageStatus
 * from @lark-automadoc/shared-types.
 */

import { computed, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PlanStatus, UsageStatus } from '@lark-automadoc/shared-types'
import {
  PRO_EXPIRING_SOON_DAYS,
  QUOTA_WARNING_THRESHOLD,
  isExpiringSoon as checkExpiringSoon,
  isQuotaWarning as checkQuotaWarning,
  isQuotaReached as checkQuotaReached,
} from '@lark-automadoc/shared-types'

export interface UsePlanOptions {
  planStatus: Ref<PlanStatus | null | undefined> | ComputedRef<PlanStatus | null | undefined>
  usageStatus?: Ref<UsageStatus | null | undefined> | ComputedRef<UsageStatus | null | undefined>
}

export interface UsePlanReturn {
  /** Display label for current plan, in the reader's language (the pricing page's plan names) */
  planDisplay: ComputedRef<string>

  /** Whether user has Pro-level access (Pro or active Trial) */
  isPro: ComputedRef<boolean>

  /** Whether user is on an active trial */
  isTrial: ComputedRef<boolean>

  /** Whether stored Pro plan has expired (needs renewal) */
  isProExpired: ComputedRef<boolean>

  /** Whether plan is expiring soon (< 15 days) */
  isExpiringSoon: ComputedRef<boolean>

  /** Whether quota is at warning level (>= 80%) */
  isQuotaWarning: ComputedRef<boolean>

  /** Whether quota has been reached */
  isQuotaReached: ComputedRef<boolean>

  /** Whether user can start a trial */
  canStartTrial: ComputedRef<boolean>

  /** Days remaining until expiration */
  daysRemaining: ComputedRef<number>

  /** Formatted expiration date string */
  expiresAtFormatted: ComputedRef<string | null>
}

export function usePlan(options: UsePlanOptions): UsePlanReturn {
  const { planStatus, usageStatus } = options

  // The pricing page's plan names, so the badge says what the plan is called
  // everywhere else on screen: hardcoded, it read "Free" beside a banner that
  // calls the same plan 免费版 or Gratuito.
  const { t } = useI18n()
  const planDisplay = computed(() => {
    switch (planStatus.value?.planType) {
      case 'pro':
        return t('pricingProName')
      case 'trial':
        return t('pricingTrialPlan')
      default:
        return t('pricingFreeName')
    }
  })

  const isPro = computed(() => {
    return planStatus.value?.hasAccess ?? false
  })

  const isTrial = computed(() => {
    return planStatus.value?.planType === 'trial' && !planStatus.value?.isExpired
  })

  const isProExpired = computed(() => {
    return planStatus.value?.storedPlan === 'pro' && (planStatus.value?.isExpired ?? false)
  })

  const isExpiringSoon = computed(() => {
    const status = planStatus.value
    if (!status) return false
    return checkExpiringSoon(status, PRO_EXPIRING_SOON_DAYS)
  })

  const isQuotaWarning = computed(() => {
    const usage = usageStatus?.value
    const plan = planStatus.value
    // Trial users have unlimited generations, skip quota checks
    if (!usage || !plan || plan.planType === 'trial') return false
    return checkQuotaWarning(usage, QUOTA_WARNING_THRESHOLD)
  })

  const isQuotaReached = computed(() => {
    const usage = usageStatus?.value
    const plan = planStatus.value
    // Trial users have unlimited generations, skip quota checks
    if (!usage || !plan || plan.planType === 'trial') return false
    return checkQuotaReached(usage)
  })

  const canStartTrial = computed(() => {
    return planStatus.value?.canStartTrial ?? false
  })

  const daysRemaining = computed(() => {
    return planStatus.value?.daysRemaining ?? 0
  })

  const expiresAtFormatted = computed(() => {
    const expiresAt = planStatus.value?.expiresAt
    if (!expiresAt) return null
    return new Date(expiresAt).toLocaleDateString()
  })

  return {
    planDisplay,
    isPro,
    isTrial,
    isProExpired,
    isExpiringSoon,
    isQuotaWarning,
    isQuotaReached,
    canStartTrial,
    daysRemaining,
    expiresAtFormatted,
  }
}
